import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { PRICING } from "@/data/site";

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <PageHero
        overline="Pricing"
        titleLines={["Simple, honest", "pricing."]}
        subtitle="Start free for 14 days. Upgrade when you're ready. Cancel anytime — no lock-in, no surprises."
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PRICING.map((tier, i) => {
            const isLink = tier.href.startsWith("/");
            const CTA = isLink ? Link : "a";
            const ctaProps = isLink ? { to: tier.href } : { href: tier.href };
            return (
              <Reveal key={tier.name} delay={i * 0.1}>
                <div
                  data-testid={`pricing-card-${tier.name.toLowerCase()}`}
                  className={`relative flex h-full flex-col rounded-2xl border p-8 transition-all duration-300 ${
                    tier.highlight
                      ? "border-brand bg-surface shadow-[0_0_60px_rgba(0,85,255,0.18)]"
                      : "border-zinc-800 bg-surface hover:border-zinc-700"
                  }`}
                >
                  {tier.highlight && (
                    <span className="absolute -top-3 left-8 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">
                      Most Popular
                    </span>
                  )}
                  <h3 className="font-heading text-xl font-semibold text-white">{tier.name}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{tier.tagline}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-heading text-5xl font-bold tracking-tighter text-white">{tier.price}</span>
                    {tier.period && <span className="mb-1.5 text-sm text-zinc-500">{tier.period}</span>}
                  </div>

                  <ul className="mt-8 flex-1 space-y-3.5">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tier.highlight ? "bg-brand text-white" : "bg-brand/15 text-brand"}`}>
                          <Check className="h-3 w-3" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <CTA
                    {...ctaProps}
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold transition-colors ${
                      tier.highlight
                        ? "bg-brand text-white hover:bg-brand-hover"
                        : "border border-white/15 bg-white/5 text-white hover:bg-white/10"
                    }`}
                  >
                    {tier.cta} <ArrowRight className="h-4 w-4" />
                  </CTA>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-14 text-center text-sm text-zinc-500">
          All plans include secure cloud access, automated reminders and free updates.
        </Reveal>
      </section>
    </div>
  );
}
